import "./App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import CookieBanner from "./components/CookieBanner";
import { useNativeDatePicker } from "./hooks/useNativeDatePicker";

// Pages chargées à la demande : le visiteur d'un lien public ne télécharge
// plus le code des espaces admin / agent (bundle initial réduit).
const HomePage = lazy(() => import("./pages/HomePage"));
const PublicVisitePage = lazy(() => import("./pages/PublicVisitePage"));
const ShortVisiteRedirect = lazy(() => import("./pages/ShortVisiteRedirect"));
const CorporateVisitePage = lazy(() => import("./pages/CorporateVisitePage"));
const ConfidentialitePage = lazy(() => import("./pages/ConfidentialitePage"));
const CGUPage = lazy(() => import("./pages/CGUPage"));
const CGVPage = lazy(() => import("./pages/CGVPage"));
const MentionsLegalesPage = lazy(() => import("./pages/MentionsLegalesPage"));
const PreferencesPage = lazy(() => import("./pages/PreferencesPage"));
const PreferencesManagePage = lazy(() => import("./pages/PreferencesManagePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const PasswordSetupPage = lazy(() => import("./pages/PasswordSetupPage"));
const RenewalRequestPage = lazy(() => import("./pages/RenewalRequestPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AgentSignupPage = lazy(() => import("./pages/AgentSignupPage"));
const SubjectAccessPage = lazy(() => import("./pages/SubjectAccessPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const AgentRechargePage = lazy(() => import("./pages/AgentRechargePage"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-500 text-sm tracking-widest uppercase">
      Chargement…
    </div>
  );
}

function ProtectedRoute({ children, role = "admin" }) {
  const { user, authUnavailable, retryAuth } = useAuth();
  if (user === null) return <RouteFallback />;
  if (!user && authUnavailable) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-neutral-950 px-6 text-center">
        <p className="text-neutral-300 text-sm" data-testid="auth-unavailable">
          Connexion au serveur impossible pour le moment. Votre session n'est pas expirée.
        </p>
        <button
          type="button"
          onClick={retryAuth}
          data-testid="auth-retry-btn"
          className="px-6 py-3 border border-[#c9a962] text-[#c9a962] text-xs tracking-[0.2em] uppercase hover:bg-[#c9a962]/10 transition-colors duration-300"
        >
          Réessayer
        </button>
      </div>
    );
  }
  if (!user) return <Navigate to={role === "agent" ? "/agent/login" : "/admin/login"} replace />;
  if (user.role !== role) {
    return <Navigate to={user.role === "agent" ? "/agent" : "/admin"} replace />;
  }
  return children;
}

export default function App() {
  useNativeDatePicker();
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/visite/:bienId" element={<PublicVisitePage />} />
                <Route path="/v/:bienId" element={<PublicVisitePage />} />
                <Route path="/s/:slug" element={<ShortVisiteRedirect />} />
                {/* Lien Location — doit précéder /c/:slug pour que le segment
                    « location » ne soit jamais pris pour un slug Corporate. */}
                <Route path="/c/location/:slug" element={<CorporateVisitePage mode="location" />} />
                <Route path="/c/:slug" element={<CorporateVisitePage />} />
                <Route path="/direct/:bienId" element={<CorporateVisitePage mode="direct" />} />
                <Route path="/confidentialite" element={<ConfidentialitePage />} />
                <Route path="/rgpd" element={<ConfidentialitePage />} />
                <Route path="/cgu" element={<CGUPage />} />
                <Route path="/cgv" element={<CGVPage />} />
                <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                <Route path="/preferences" element={<PreferencesPage />} />
                <Route path="/preferences/:token" element={<PreferencesManagePage />} />
                <Route path="/renouveler/:token" element={<RenewalRequestPage />} />
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/agent/login" element={<LoginPage />} />
                <Route path="/agent/recharge" element={<AgentRechargePage />} />
                <Route path="/agent/password/setup/:token" element={<PasswordSetupPage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute role="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agent"
                  element={
                    <ProtectedRoute role="agent">
                      <AgentDashboard />
                    </ProtectedRoute>
                  }
                />
                {/* Page non listée : accessible uniquement par URL directe (noindex, nofollow) */}
                <Route path="/formulaire" element={<AgentSignupPage />} />
                <Route path="/mes-donnees/:token" element={<SubjectAccessPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <CookieBanner />
          </BrowserRouter>
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved === "light" ? "light" : "dark"} position="top-right" richColors />;
}
